const SHA256_CONSTANTS = Object.freeze([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
    0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]);

/**
 * Rotate one unsigned 32-bit word right.
 *
 * @param {number} value Unsigned word.
 * @param {number} shift Bit count.
 * @returns {number} Rotated word.
 */
function rotateRight(value, shift)
{
    return (value >>> shift) | (value << (32 - shift));
}

/**
 * Normalize an exact byte range without copying it.
 *
 * @param {Uint8Array|ArrayBuffer|ArrayBufferView} input Candidate byte range.
 * @returns {Uint8Array} Exact byte view.
 */
function normalizeBytes(input)
{
    if (input instanceof Uint8Array)
    {
        return input;
    }
    if (input instanceof ArrayBuffer)
    {
        return new Uint8Array(input);
    }
    if (ArrayBuffer.isView(input))
    {
        return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
    }
    throw new TypeError(
        "SHA-256 input must be Uint8Array, ArrayBuffer, or ArrayBufferView bytes"
    );
}

/**
 * Browser-safe synchronous SHA-256 over an exact byte view.
 *
 * @param {Uint8Array|ArrayBuffer|ArrayBufferView} input Bytes to hash.
 * @returns {string} Lower-case hexadecimal digest.
 */
export function sha256Bytes(input)
{
    const inputBytes = normalizeBytes(input);
    const paddedLength = Math.ceil((inputBytes.length + 9) / 64) * 64;
    const bytes = new Uint8Array(paddedLength);
    bytes.set(inputBytes);
    bytes[inputBytes.length] = 0x80;
    let bitLength = BigInt(inputBytes.length) * 8n;
    for (let index = 0; index < 8; index += 1)
    {
        bytes[paddedLength - 1 - index] = Number(bitLength & 0xffn);
        bitLength >>= 8n;
    }
    const hash = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
    ]);
    const schedule = new Uint32Array(64);
    for (let offset = 0; offset < bytes.length; offset += 64)
    {
        for (let index = 0; index < 16; index += 1)
        {
            const word = offset + index * 4;
            schedule[index] = (
                bytes[word] * 0x1000000
                + bytes[word + 1] * 0x10000
                + bytes[word + 2] * 0x100
                + bytes[word + 3]
            ) >>> 0;
        }
        for (let index = 16; index < 64; index += 1)
        {
            const left = schedule[index - 15];
            const right = schedule[index - 2];
            const sigma0 = rotateRight(left, 7)
                ^ rotateRight(left, 18) ^ (left >>> 3);
            const sigma1 = rotateRight(right, 17)
                ^ rotateRight(right, 19) ^ (right >>> 10);
            schedule[index] = (
                schedule[index - 16] + sigma0
                + schedule[index - 7] + sigma1
            ) >>> 0;
        }
        let a = hash[0];
        let b = hash[1];
        let c = hash[2];
        let d = hash[3];
        let e = hash[4];
        let f = hash[5];
        let g = hash[6];
        let h = hash[7];
        for (let index = 0; index < 64; index += 1)
        {
            const sum1 = rotateRight(e, 6)
                ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temporary1 = (
                h + sum1 + choose
                + SHA256_CONSTANTS[index] + schedule[index]
            ) >>> 0;
            const sum0 = rotateRight(a, 2)
                ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temporary2 = (sum0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temporary1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temporary1 + temporary2) >>> 0;
        }
        hash[0] = (hash[0] + a) >>> 0;
        hash[1] = (hash[1] + b) >>> 0;
        hash[2] = (hash[2] + c) >>> 0;
        hash[3] = (hash[3] + d) >>> 0;
        hash[4] = (hash[4] + e) >>> 0;
        hash[5] = (hash[5] + f) >>> 0;
        hash[6] = (hash[6] + g) >>> 0;
        hash[7] = (hash[7] + h) >>> 0;
    }
    return Array.from(hash, (word) =>
        word.toString(16).padStart(8, "0")).join("");
}

/**
 * Browser-safe synchronous SHA-256 for a UTF-8 string.
 *
 * @param {string} text UTF-16 JavaScript string encoded as UTF-8.
 * @returns {string} Lower-case hexadecimal digest.
 */
export function sha256Utf8(text)
{
    if (typeof text !== "string")
    {
        throw new TypeError("SHA-256 input must be a string");
    }
    return sha256Bytes(new TextEncoder().encode(text));
}
